import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
} from "@mui/material";
import { formatWeight, formatReturns, getReturnsColor, buildHoldingsRows } from "./utility";


// Column definitions — numeric: true drives the sort comparator
const COLUMNS = [
  { key: "symbol", label: "Symbol", align: "left", numeric: false },
  { key: "description", label: "Description", align: "left", numeric: false },
  { key: "side", label: "Side", align: "left", numeric: false },
  { key: "signalWeight", label: "Signal Weight", align: "right", numeric: true },
  { key: "mktAdjWeight", label: "Mkt Adj Weight", align: "right", numeric: true },
  { key: "returns", label: "Returns", align: "right", numeric: true },
];

const SignalSetHoldings = ({ marketAdjustedWeights }) => {
  const theme = useTheme();
  const [orderBy, setOrderBy] = useState("symbol");
  const [order, setOrder] = useState("asc");
  const [tableRows, setTableRows] = useState([]);

  useEffect(() => {
    const rows = buildHoldingsRows(marketAdjustedWeights);
    setTableRows(rows);
  }, [marketAdjustedWeights]);

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
  };

  // Re-sort only when the active column or direction changes
  const sortedRows = useMemo(() => {
    return [...tableRows].sort((rowA, rowB) => {
      const valueA = rowA[orderBy];
      const valueB = rowB[orderBy];
      // String columns use locale-aware comparison - numeric columns use arithmetic diff
      if (typeof valueA === "string") {
        return order === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }
      return order === "asc" ? valueA - valueB : valueB - valueA;
    });
  }, [tableRows, orderBy, order]);

  const signalSetLastUpdatedDate = useMemo(() => {
    const ts = marketAdjustedWeights?.date;
    if (ts == null) return "—";
    const d = new Date(Number(ts));
    return d.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, [marketAdjustedWeights?.date]);
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, my: 3 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: "1.2rem" }}
        >
          Signal Set Holdings
        </Typography>
        <Typography color="textSecondary" variant="h5" sx={{ lineHeight: 1.5 }}>
          Last Updated:{" "}
        </Typography>
        <Typography color="textSecondary" variant="h5" fontWeight={600} sx={{ lineHeight: 1.5 }}>
          {signalSetLastUpdatedDate}
        </Typography>
      </Box>
      <TableContainer
        component={Paper}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align}
                  sortDirection={orderBy === column.key ? order : false}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.grey[100],
                    color: theme.palette.text.primary,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === column.key}
                    direction={orderBy === column.key ? order : "asc"}
                    onClick={() => handleSort(column.key)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row) => (
              <TableRow key={row.id || row.symbol} hover>
                <TableCell sx={{ fontWeight: 600 }}>{row.symbol}</TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell>{row.side}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>
                  {formatWeight(row.signalWeight)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>
                  {formatWeight(row.mktAdjWeight)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: 500, color: getReturnsColor(row.returns, theme) }}
                >
                  {formatReturns(row.returns)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>

  );
};

export default SignalSetHoldings;
